/**
 * ProjectsPage — lightweight Projects module (Brian 2026-09-09 §8.10: not a PM
 * suite). Company-scoped project cards + role-gated create/edit, BOM/CD/RFDS
 * document upload/download/delete, crew (Team) assignment, and the §4.4.5 /
 * §4.3 lightweight fields (structure info, stakeholders, notes).
 *
 * §8.10 fast-follow seam: an "AI pre-fill" placeholder section is labeled but
 * intentionally inert — AI proposes / human confirms / source cited lands in a
 * later slice; no AI code ships here.
 *
 * Role gating mirrors the DashboardPage localStorage 'user' pattern:
 * write actions visible only to ADMIN / PROJECT_MANAGER /
 * CONSTRUCTION_MANAGER / SAFETY_MANAGER; every authenticated user of the
 * company can list + download.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { readFileBytes, CLOUD_FILE_ERROR } from '@/utils/fileRead';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  FolderKanban,
  FileText,
  Download,
  Trash2,
  Users,
  Upload,
  X,
  Loader2,
  Plus,
  Pencil,
  ClipboardList,
  Building2,
  Sparkles,
} from 'lucide-react';
import { api } from '../utils/api';
import ProjectCrewSection from './ProjectCrewSection';
import {
  EMPTY_STAKEHOLDER,
  type ProjectFormPayload,
  type ProjectRecord,
  type ProjectStakeholder,
} from '@/types/project';

const WRITE_ROLES = ['ADMIN', 'PROJECT_MANAGER', 'CONSTRUCTION_MANAGER', 'SAFETY_MANAGER'] as const;

const DOC_TYPES = ['BOM', 'CD', 'RFDS', 'SCOPE', 'PERMIT', 'OTHER'] as const;

const DOC_TYPE_STYLES: Record<string, string> = {
  BOM: 'bg-blue-100 text-blue-700',
  CD: 'bg-indigo-100 text-indigo-700',
  RFDS: 'bg-teal-100 text-teal-700',
  SCOPE: 'bg-amber-100 text-amber-700',
  PERMIT: 'bg-purple-100 text-purple-700',
  OTHER: 'bg-gray-100 text-gray-600',
};

const STATUS_STYLES: Record<string, string> = {
  PLANNING: 'bg-amber-100 text-amber-700',
  ACTIVE: 'bg-green-100 text-green-700',
  ON_HOLD: 'bg-orange-100 text-orange-700',
  COMPLETED: 'bg-blue-100 text-blue-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

const EMPTY_FORM: ProjectFormPayload = {
  name: '',
  siteAddress: '',
  carrierName: '',
  carrierId: '',
  city: '',
  state: '',
  zipCode: '',
  startDate: '',
  endDate: '',
  structureInfo: '',
  notes: '',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function currentUserRole(): string | null {
  try {
    const raw = localStorage.getItem('user');
    return raw ? (JSON.parse(raw)?.role ?? null) : null;
  } catch {
    return null;
  }
}


function apiErrorMessage(e: unknown, fallback: string): string {
  const r = e as { response?: { data?: { message?: string } } };
  return r?.response?.data?.message ?? fallback;
}

function formFromProject(p: ProjectRecord): ProjectFormPayload {
  return {
    name: p.name ?? '',
    siteAddress: p.siteAddress ?? '',
    carrierName: p.carrierName ?? '',
    carrierId: p.carrierId ?? '',
    city: p.city ?? '',
    state: p.state ?? '',
    zipCode: p.zipCode ?? '',
    startDate: p.startDate ? p.startDate.slice(0, 10) : '',
    endDate: p.endDate ? p.endDate.slice(0, 10) : '',
    structureInfo: p.structureInfo ?? '',
    notes: p.notes ?? '',
  };
}

function payloadFromForm(
  form: ProjectFormPayload,
  stakeholders: ProjectStakeholder[]
): ProjectFormPayload {
  const payload: ProjectFormPayload = {
    name: form.name,
    siteAddress: form.siteAddress,
    structureInfo: form.structureInfo || null,
    notes: form.notes || null,
  };
  for (const k of ['carrierName', 'carrierId', 'city', 'state', 'zipCode'] as const) {
    if (form[k]) payload[k] = form[k];
  }
  if (form.startDate) payload.startDate = new Date(form.startDate).toISOString();
  if (form.endDate) payload.endDate = new Date(form.endDate).toISOString();
  // Only send stakeholders when at least one row has content.
  const filled = stakeholders.filter((s) => s.name.trim() || s.role.trim() || s.org.trim());
  payload.stakeholders = filled.length
    ? filled.map((s) => ({
        name: s.name.trim(),
        role: s.role.trim(),
        org: s.org.trim(),
        ...(s.phone?.trim() ? { phone: s.phone.trim() } : {}),
      }))
    : null;
  return payload;
}

export function ProjectsPage() {
  const [role, setRole] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<ProjectFormPayload>({ ...EMPTY_FORM });
  const [stakeholders, setStakeholders] = useState<ProjectStakeholder[]>([{ ...EMPTY_STAKEHOLDER }]);
  const [saving, setSaving] = useState(false);

  const [openProjectId, setOpenProjectId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ProjectRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadDocType, setUploadDocType] = useState<string>('BOM');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const canWrite = role != null && (WRITE_ROLES as readonly string[]).includes(role);
  const currentUserId = (() => {
    try { return Number(JSON.parse(localStorage.getItem('user') || '{}')?.id) || null; } catch { return null; }
  })();

  const load = useCallback(() => {
    setLoading(true);
    api
      .get('/projects')
      .then((res) => setProjects((res.data?.data ?? res.data ?? []) as ProjectRecord[]))
      .catch((e: { response?: { data?: { message?: string } } }) => setError(apiErrorMessage(e, 'Could not load projects.')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setRole(currentUserRole());
    load();
  }, [load]);

  const openDetail = async (id: number) => {
    setOpenProjectId(id);
    setDetail(null);
    setEditing(false);
    setDetailLoading(true);
    setError('');
    try {
      const res = await api.get(`/projects/${id}`);
      setDetail(res.data?.data ?? res.data);
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Could not load project.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setOpenProjectId(null);
    setDetail(null);
    setEditing(false);
  };

  const beginEdit = () => {
    if (!detail) return;
    setForm(formFromProject(detail));
    setStakeholders(
      detail.stakeholders?.length ? detail.stakeholders.map((s) => ({ ...s })) : [{ ...EMPTY_STAKEHOLDER }]
    );
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!detail) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await api.patch(`/projects/${detail.id}`, payloadFromForm(form, stakeholders));
      setNotice('Project updated.');
      setEditing(false);
      await openDetail(detail.id);
      load();
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Could not update project.'));
    } finally {
      setSaving(false);
    }
  };

  const createProject = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = payloadFromForm(form, stakeholders);
      await api.post('/projects', payload);
      setNotice('Project created.');
      setForm({ ...EMPTY_FORM });
      setStakeholders([{ ...EMPTY_STAKEHOLDER }]);
      setShowCreate(false);
      load();
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Could not create project.'));
    } finally {
      setSaving(false);
    }
  };

  const uploadDocument = async () => {
    if (!detail || !fileRef.current?.files?.length) return;
    const file = fileRef.current.files[0];
    setUploading(true);
    setError('');
    setNotice('');
    try {
      // Pre-flight: cloud-app "virtual" files (Google Drive on Android) carry name/type
      // but fail on byte read. Reading first gives a clear error instead of a dead POST.
      const bytes = await readFileBytes(file);
      if (bytes === null) {
        setError(CLOUD_FILE_ERROR);
        setUploading(false);
        return;
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('docType', uploadDocType);
      await api.post(`/projects/${detail.id}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setNotice('Document uploaded.');
      if (fileRef.current) fileRef.current.value = '';
      await openDetail(detail.id);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; code?: string; message?: string };
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      const netDetail = [err?.code, err?.message].filter(Boolean).join(' / ');
      const isNetworkError = !status && (err?.code === 'ERR_NETWORK' || /network/i.test(err?.message || ''));
      setError(
        status === 401 ? 'Your session expired — please sign in again, then retry the upload.'
        : status === 413 ? 'File is too large (limit 20 MB).'
        : status ? (msg || `Upload failed (HTTP ${status}). Check your connection and retry.`)
        : isNetworkError
          ? 'Upload failed before leaving your device. If the file was picked from Google Drive, download it to the phone first (long-press in Drive > Download), then pick it from Files.'
          : `Could not reach the server to upload${netDetail ? ` (${netDetail})` : ''}. Check your connection and retry.`
      );
    } finally {
      setUploading(false);
    }
  };

  // Luna audit (MEDIUM): a plain <a href> sends no Authorization header — the
  // download endpoint is token-auth, so fetch the blob through the api client
  // (which carries the bearer token) and trigger a save from the object URL.
  const downloadDocument = useCallback(async (projectId: number, docId: number, originalName: string) => {
    try {
      const res = await api.get(`/projects/${projectId}/documents/${docId}/download`, { responseType: 'blob' });
      const blob: Blob = res instanceof Blob ? res : ((res as { data?: Blob }).data as Blob);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed — you may not have access to this document.');
    }
  }, []);

  const deleteDocument = async (projectId: number, docId: number) => {
    setError('');
    setNotice('');
    try {
      await api.delete(`/projects/${projectId}/documents/${docId}`);
      setNotice('Document deleted.');
      await openDetail(projectId);
      load();
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Could not delete document.'));
    }
  };

  const setField = (k: keyof ProjectFormPayload, v: string) => setForm({ ...form, [k]: v });
  const setStakeholderField = (i: number, k: keyof ProjectStakeholder, v: string) =>
    setStakeholders(stakeholders.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  const stakeholderRows = (
    <div className="space-y-2">
      {stakeholders.map((s, i) => (
        <div key={i} data-testid={`stakeholder-row-${i}`} className="grid sm:grid-cols-4 gap-2">
          <input
            data-testid={`stakeholder-name-${i}`}
            placeholder="Name *"
            value={s.name}
            onChange={(e) => setStakeholderField(i, 'name', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            data-testid={`stakeholder-role-${i}`}
            placeholder="Role *"
            value={s.role}
            onChange={(e) => setStakeholderField(i, 'role', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            data-testid={`stakeholder-org-${i}`}
            placeholder="Organization *"
            value={s.org}
            onChange={(e) => setStakeholderField(i, 'org', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <div className="flex gap-2">
            <input
              data-testid={`stakeholder-phone-${i}`}
              placeholder="Phone"
              value={s.phone ?? ''}
              onChange={(e) => setStakeholderField(i, 'phone', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            {stakeholders.length > 1 && (
              <button
                type="button"
                data-testid={`stakeholder-remove-${i}`}
                onClick={() => setStakeholders(stakeholders.filter((_, idx) => idx !== i))}
                className="px-2 text-gray-400 hover:text-red-600"
                title="Remove stakeholder"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}
      <button
        type="button"
        data-testid="stakeholder-add"
        onClick={() => setStakeholders([...stakeholders, { ...EMPTY_STAKEHOLDER }])}
        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
      >
        <Plus className="h-3.5 w-3.5" /> Add stakeholder
      </button>
    </div>
  );

  const lightweightFields = (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project name *</label>
          <input value={form.name} onChange={(e) => setField('name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Site address *</label>
          <input value={form.siteAddress} onChange={(e) => setField('siteAddress', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Carrier name</label>
          <input value={form.carrierName ?? ''} onChange={(e) => setField('carrierName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Carrier ID</label>
          <input value={form.carrierId ?? ''} onChange={(e) => setField('carrierId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
          <input value={form.city ?? ''} onChange={(e) => setField('city', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
          <input value={form.state ?? ''} onChange={(e) => setField('state', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">ZIP code</label>
          <input value={form.zipCode ?? ''} onChange={(e) => setField('zipCode', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
          <input type="date" value={form.startDate ?? ''} onChange={(e) => setField('startDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
          <input type="date" value={form.endDate ?? ''} onChange={(e) => setField('endDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Building2 className="inline h-4 w-4 mr-1" />Structure info (type, height, site notes)
        </label>
        <textarea
          data-testid="structure-info-input"
          value={form.structureInfo ?? ''}
          onChange={(e) => setField('structureInfo', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>
      <div className="mt-4" data-testid="stakeholders-field-group">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Users className="inline h-4 w-4 mr-1" />Stakeholders (name, role, organization)
        </label>
        {stakeholderRows}
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          data-testid="notes-input"
          value={form.notes ?? ''}
          onChange={(e) => setField('notes', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>
      {/* §8.10 fast-follow seam: AI pre-fill (human-confirm cards) — placeholder only. */}
      <div data-testid="ai-prefill-placeholder" className="mt-4 border border-dashed border-gray-300 rounded-lg p-3 text-xs text-gray-500 flex items-center gap-2">
        <Sparkles className="h-4 w-4 flex-shrink-0" />
        <span>
          AI pre-fill (coming soon): upload your bid package, site drawings, or scope docs and confirm
          pre-filled fields from cited sources — nothing is auto-saved without your review.
        </span>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        {/* Header — responsive: stacks on mobile (flex-col sm:flex-row) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FolderKanban className="h-6 w-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">Projects</h1>
              <p className="text-sm text-gray-500">{projects.length} {projects.length === 1 ? 'project' : 'projects'}</p>
            </div>
          </div>
          {canWrite && (
            <button
              data-testid="new-project-btn"
              onClick={() => { setShowCreate(!showCreate); setError(''); setNotice(''); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {showCreate ? <X className="h-4 w-4" /> : <FolderKanban className="h-4 w-4" />}
              {showCreate ? 'Close' : 'New Project'}
            </button>
          )}
        </div>

        {notice && <div data-testid="notice" className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm">{notice}</div>}
        {error && !showCreate && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

        {/* Create form — role-gated inline panel */}
        {canWrite && showCreate && (
          <div data-testid="create-form" className="mb-6 bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">New project</h2>
            {lightweightFields}
            {/* Team assignment select hidden until Team CRUD exists — server
                has no team assignment endpoints today. */}
            <button
              data-testid="create-submit"
              onClick={createProject}
              disabled={saving || !form.name.trim() || !form.siteAddress.trim()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create project'}
            </button>
          </div>
        )}

        {/* Project cards */}
        <div className="bg-white rounded-xl shadow border border-gray-100 divide-y divide-gray-100">
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading…</div>
          ) : projects.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">
              No projects yet.{canWrite ? ' Create your first project above.' : ''}
            </div>
          ) : (
            projects.map((p) => (
              <div key={p.id} data-testid={`project-card-${p.id}`} className="px-5 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <button className="text-left min-w-0" onClick={() => (openProjectId === p.id ? closeDetail() : openDetail(p.id))}>
                    <p className="font-semibold text-gray-900 text-sm truncate">{p.name}</p>
                    <p className="text-xs text-gray-500 truncate">{p.siteAddress}{p.city ? ` · ${p.city}` : ''}{p.state ? `, ${p.state}` : ''}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[p.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {p.status}
                      </span>
                      <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {p._count?.documents ?? 0} {(p._count?.documents ?? 0) === 1 ? 'doc' : 'docs'}
                      </span>
                    </div>
                  </button>
                  {/* Crew (Team) assignment dropdown hidden until Team CRUD
                      exists — server has no team assignment endpoints. */}
                </div>

                {/* Detail section: edit form + documents + crew + linked surfaces */}
                {openProjectId === p.id && (
                  <div data-testid={`project-detail-${p.id}`} className="mt-4 pt-4 border-t border-gray-100">
                    {detailLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading project…
                      </div>
                    ) : detail ? (
                      <>
                        {/* Lightweight detail header + edit toggle */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{detail.name}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {detail.siteAddress}{detail.city ? ` · ${detail.city}` : ''}{detail.state ? `, ${detail.state}` : ''}
                            </p>
                          </div>
                          {canWrite && (
                            <button
                              data-testid={`edit-project-${p.id}`}
                              onClick={() => (editing ? setEditing(false) : beginEdit())}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 self-start sm:self-auto"
                            >
                              {editing ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                              {editing ? 'Cancel edit' : 'Edit project'}
                            </button>
                          )}
                        </div>

                        {editing ? (
                          <div data-testid={`edit-form-${p.id}`} className="mb-4">
                            {lightweightFields}
                            <button
                              data-testid={`edit-save-${p.id}`}
                              onClick={saveEdit}
                              disabled={saving || !form.name.trim() || !form.siteAddress.trim()}
                              className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                              {saving ? 'Saving…' : 'Save changes'}
                            </button>
                          </div>
                        ) : (
                          /* Read-only lightweight fields */
                          <div data-testid={`lightweight-view-${p.id}`} className="mb-4 text-xs text-gray-600 space-y-1">
                            {detail.structureInfo && (
                              <p><span className="font-medium text-gray-700">Structure:</span> {detail.structureInfo}</p>
                            )}
                            {detail.stakeholders?.length ? (
                              <div>
                                <span className="font-medium text-gray-700">Stakeholders:</span>
                                <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                  {detail.stakeholders.map((s, i) => (
                                    <li key={i}>{s.name} — {s.role}{s.org ? `, ${s.org}` : ''}{s.phone ? ` · ${s.phone}` : ''}</li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                            {detail.notes && <p><span className="font-medium text-gray-700">Notes:</span> {detail.notes}</p>}
                            {!detail.structureInfo && !detail.stakeholders?.length && !detail.notes && (
                              <p className="text-gray-400">No structure info, stakeholders, or notes yet.{canWrite ? ' Use Edit project to add them.' : ''}</p>
                            )}
                          </div>
                        )}

                        {/* Linked module surfaces (§8.10: the container makes other modules smart) */}
                        <div className="flex flex-wrap gap-2 mb-4" data-testid={`linked-surfaces-${p.id}`}>
                          {(detail._count?.constructionPlans ?? 0) > 0 && (
                            <Link
                              to="/construction-plans"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                            >
                              <ClipboardList className="h-3.5 w-3.5" />
                              Construction plans ({detail._count?.constructionPlans})
                            </Link>
                          )}
                          {(detail._count?.jhas ?? 0) > 0 && (
                            <Link
                              to="/jha"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100"
                            >
                              <ClipboardList className="h-3.5 w-3.5" />
                              JHAs ({detail._count?.jhas})
                            </Link>
                          )}
                        </div>

                        {/* Documents panel */}
                        {canWrite && (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3 flex-wrap">
                            <div>
                              <input
                                data-testid={`upload-input-${p.id}`}
                                type="file"
                                ref={(el) => { fileRef.current = el; }}
                                className="text-xs text-gray-600 file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-700 file:text-xs"
                              />
                              <p className="text-[11px] text-gray-400 mt-1">Files stored in Google Drive or other cloud apps must be downloaded to your device first — pick from <b>Files / Downloads</b>, not Drive.</p>
                            </div>
                            <select
                              data-testid={`doc-type-select-${p.id}`}
                              value={uploadDocType}
                              onChange={(e) => setUploadDocType(e.target.value)}
                              className="text-xs px-2 py-1.5 border border-gray-300 rounded-lg"
                            >
                              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <button
                              data-testid={`upload-btn-${p.id}`}
                              onClick={uploadDocument}
                              disabled={uploading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                              <Upload className="h-3.5 w-3.5" />
                              {uploading ? 'Uploading…' : 'Upload document'}
                            </button>
                          </div>
                        )}

                        {!detail.documents?.length ? (
                          <p className="text-xs text-gray-500 py-2">No documents uploaded yet.</p>
                        ) : (
                          <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                            {detail.documents.map((d) => (
                              <div key={d.id} className="flex items-center justify-between gap-2 px-3 py-2">
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-gray-900 truncate">{d.originalName}</p>
                                  <p className="text-[11px] text-gray-500">
                                    {formatBytes(d.sizeBytes)} · {new Date(d.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${DOC_TYPE_STYLES[d.docType] ?? 'bg-gray-100 text-gray-600'}`}>
                                    {d.docType}
                                  </span>
                                  <button
                                    data-testid={`download-${d.id}`}
                                    onClick={() => downloadDocument(p.id, d.id, d.originalName)}
                                    className="p-1.5 text-gray-500 hover:text-blue-600"
                                    title="Download"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                  {canWrite && (
                                    <button
                                      data-testid={`delete-doc-${d.id}`}
                                      onClick={() => deleteDocument(p.id, d.id)}
                                      className="p-1.5 text-gray-500 hover:text-red-600"
                                      title="Delete"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <ProjectCrewSection
                          projectId={p.id}
                          projectName={p.name}
                          canWrite={canWrite}
                          currentUserId={currentUserId}
                        />
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectsPage;